import { z } from "zod";
import { addDays, isAfter, parseISO } from "date-fns";

export const createSchema = () =>
  z
    .object({
      companyId: z.string({
        required_error: "Selecione uma empresa através de seu nome.",
      }),
      packageId: z.string({
        required_error: "Selecione um pacote através de seu nome.",
      }),
      contractResponsibleUser: z.string().optional(),
      roomResponsibleUser: z.string().optional(),
      coworkingId: z.string({
        required_error: "Selecione um coworking através de seu nome.",
      }),
      privateOfficeId: z.string().optional(),
      startDate: z
        .string({ required_error: "Insira uma data de início." })
        .refine((date) => !isNaN(Date.parse(date)), {
          message: "Insira uma data válida.",
        }),
      endDate: z.string().optional(),
      members: z.number({
        required_error: "Insira o número de membros.",
      }),
      monthlyCredits: z
        .number({
          required_error: "Insira o número de créditos mensais.",
        })
        .min(1, {
          message: "O número de créditos mensais deve ser maior ou igual 1.",
        })
        .multipleOf(0.01, {
          message:
            "O número de créditos mensais deve ter no máximo 2 casas decimais.",
        }),
      partnerValue: z.string({
        required_error: "Insira o valor do parceiro.",
      }),
      takeRatePercentage: z
        .number({
          required_error: "Insira a taxa de take rate.",
        })
        .min(10, {
          message: "A taxa de take rate deve ser maior que 10.",
        }),
      dealId: z.string({
        required_error: "Insira o ID do deal do hubspot.",
      }),
      contractType: z.string({
        required_error: "Selecione o tipo de contrato.",
      }),
      repass: z.number({
        required_error: "Insira o valor do repasse.",
      }),
      noticeInDaysClient: z.number({
        required_error: "Insira o período de aviso prévio do cliente.",
      }),
      noticeInDaysPartner: z.number({
        required_error: "Insira o período de aviso prévio do parceiro.",
      }),
      hasUndeterminedTime: z.boolean().default(false),
      expectedDurationMonths: z.number({
        required_error: "Insira a vigência prevista (em meses).",
      }),
      notes: z.string().optional(),
      renewalIndicator: z.string({
        required_error: "Selecione o índice de reajuste.",
      }),
      acceptanceTerm: z.string({
        required_error: "Insira o termo de aceite.",
      }),
      knowledgeTerm: z.string({
        required_error: "Insira o termo de ciência.",
      }),
      flowType: z.enum(["default", "upsell"]),
      poType: z.enum(["pronto", "personalizado", "sob_medida"]).optional(),
    })
    .refine(
      (data) => {
        if (data.hasUndeterminedTime) {
          return true;
        }
        if (!data.endDate) {
          return false;
        }
        return !isNaN(Date.parse(data.endDate));
      },
      {
        message: "Insira uma data de término válida.",
        path: ["endDate"],
      },
    )
    .refine(
      (data) => {
        if (data.hasUndeterminedTime || !data.endDate) {
          return true;
        }
        const startDate = parseISO(data.startDate);
        const endDate = parseISO(data.endDate);
        const minEndDate = addDays(startDate, data.noticeInDaysClient);
        return isAfter(endDate, minEndDate);
      },
      {
        message:
          "A data de término deve ser após o período de aviso prévio do cliente.",
        path: ["endDate"],
      },
    )
    .refine(
      (data) => {
        if (
          data.contractType === "po" ||
          data.contractType === "extra-service"
        ) {
          return !!data.privateOfficeId;
        }

        return true;
      },
      {
        message: "Selecione um escritório privativo.",
        path: ["privateOfficeId"],
      },
    )
    .refine((data) => data.contractType !== "po" || !!data.poType, {
      message: "Selecione o tipo de escritório.",
      path: ["poType"],
    });

export const schema = createSchema();
